<script lang="ts">
  import { onMount } from 'svelte';
  import { Chart, registerables } from 'chart.js';
  import type { Summary, Transaction } from '../lib/types.js';

  type Props = {
    summary: Summary;
    transactions: Transaction[];
  };

  let { summary, transactions }: Props = $props();
  let canvas: HTMLCanvasElement;
  let chart: Chart | null = null;

  // Predefined color palette for categories
  const categoryColors = [
    '#2563eb', // primary
    '#10b981', // success
    '#f59e0b', // warning
    '#ef4444', // danger
    '#8b5cf6', // purple
    '#06b6d4', // cyan
    '#f97316', // orange
    '#ec4899', // pink
    '#6366f1', // indigo
    '#84cc16', // lime
  ];

  onMount(() => {
    Chart.register(...registerables);

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  });

  // Re-render chart when summary or transactions change, or canvas is available
  $effect(() => {
    if (canvas && summary && transactions) {
      renderChart();
    }
  });

  function renderChart() {
    if (!summary || !summary.byMonth || !transactions || transactions.length === 0 || !canvas) {
      return;
    }

    // Destroy existing chart before creating new one
    if (chart) {
      chart.destroy();
      chart = null;
    }

    // Also check if Chart.js has an existing chart instance for this canvas
    const existingChart = Chart.getChart(canvas);
    if (existingChart) {
      existingChart.destroy();
    }

    // Calculate spending by category per month
    const monthCategorySpending: Record<string, Record<string, number>> = {};

    transactions.forEach(t => {
      // Only include expenses (negative amounts)
      if (t.amount >= 0) return;

      // Skip excluded transactions
      if (t.excluded) return;

      // Skip income-related, transfer, and savings/investment categories
      const category = t.category || 'Uncategorized';
      const categoryLower = category.toLowerCase();
      const excludedCategories = ['income', 'salary & wages', 'interest & dividends',
                                  'income - other', 'bonus & windfalls', 'tax return',
                                  'transfer', 'credit card payment',
                                  'investment', '529', 'retirement', 'savings'];
      if (excludedCategories.includes(categoryLower)) return;

      // Extract month from date (YYYY-MM)
      const month = t.date.substring(0, 7);

      if (!monthCategorySpending[month]) {
        monthCategorySpending[month] = {};
      }

      if (!monthCategorySpending[month][category]) {
        monthCategorySpending[month][category] = 0;
      }

      monthCategorySpending[month][category] += Math.abs(t.amount);
    });

    // Sort months chronologically and limit to last 12 months
    const allMonths = Object.keys(monthCategorySpending).sort();
    const months = allMonths.slice(-12); // Take last 12 months

    if (months.length === 0) {
      return;
    }

    // Get all unique categories across all months
    const allCategories = new Set<string>();
    months.forEach(month => {
      Object.keys(monthCategorySpending[month]).forEach(cat => allCategories.add(cat));
    });

    // Sort categories by total spending (descending) and take top 15
    const categoryTotals: Record<string, number> = {};
    allCategories.forEach(cat => {
      categoryTotals[cat] = 0;
      months.forEach(month => {
        categoryTotals[cat] += monthCategorySpending[month][cat] || 0;
      });
    });

    const topCategories = Object.entries(categoryTotals)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 15)
      .map(([cat]) => cat);

    // Group remaining categories as "Other"
    const displayCategories = [...topCategories];
    const hasOther = allCategories.size > 15;
    if (hasOther) {
      displayCategories.push('Other');
    }

    // Prepare datasets for each category
    const datasets = displayCategories.map((category, index) => {
      const data = months.map(month => {
        if (category === 'Other') {
          // Sum all categories not in top 15
          let sum = 0;
          Object.keys(monthCategorySpending[month]).forEach(cat => {
            if (!topCategories.includes(cat)) {
              sum += monthCategorySpending[month][cat];
            }
          });
          return sum;
        } else {
          return monthCategorySpending[month][category] || 0;
        }
      });

      const color = categoryColors[index % categoryColors.length];

      return {
        label: category,
        data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
      };
    });

    // Format month labels (e.g., "2026-01" -> "Jan 2026")
    const labels = months.map(month => {
      const [year, monthNum] = month.split('-');
      const date = new Date(parseInt(year), parseInt(monthNum) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });

    // Create the chart
    chart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            position: 'right',
            align: 'start',
            labels: {
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-text-tertiary').trim(),
              font: {
                size: 11
              },
              boxWidth: 12,
              padding: 8,
              usePointStyle: true,
              pointStyle: 'circle'
            }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                label += new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(context.parsed.y);

                // Calculate percentage of total for this month
                const monthIndex = context.dataIndex;
                const total = context.chart.data.datasets.reduce((sum, dataset) => {
                  return sum + (dataset.data[monthIndex] as number || 0);
                }, 0);
                const percentage = total > 0 ? (context.parsed.y / total * 100).toFixed(1) : 0;
                label += ` (${percentage}%)`;

                return label;
              },
              footer: function(tooltipItems) {
                const total = tooltipItems.reduce((sum, item) => sum + item.parsed.y, 0);
                return 'Total: ' + new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD'
                }).format(total);
              }
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            ticks: {
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-text-tertiary').trim(),
              maxRotation: 45,
              minRotation: 0
            },
            grid: {
              display: false
            }
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-text-tertiary').trim(),
              callback: function(value) {
                return '$' + value.toLocaleString();
              }
            },
            grid: {
              color: getComputedStyle(document.documentElement).getPropertyValue('--color-border').trim()
            }
          }
        }
      }
    });
  }
</script>

<div class="chart-container">
  <canvas bind:this={canvas}></canvas>
</div>

<style>
  .chart-container {
    position: relative;
    width: 100%;
    height: 320px;
  }
</style>
